import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate, useReducedMotion } from "framer-motion";
import Lenis from "lenis";

export const EASE = [0.22, 1, 0.36, 1];
export const WA_NUMBER = "917014505458";
export const WA_BASE_TEXT = "Hi, I'd like to apply for Hitakari House membership.";

export const waLink = (extra = "") => {
  const text = encodeURIComponent(WA_BASE_TEXT + extra);
  return `https://wa.me/${WA_NUMBER}?text=${text}`;
};

/* Meta Pixel Lead event helper. Safe no-op if fbq isn't loaded (ad-blockers, tests). */
export const fbLead = (contentName) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try { window.fbq("track", "Lead", { content_name: contentName }); } catch (_) {}
  }
};

/* Smooth momentum scrolling */
export const useLenis = () => {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.25, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [reduce]);
};

/* Fade + rise reveal on scroll */
export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-12% 0px -12% 0px" });
  const M = motion[as] || motion.div;
  return (
    <M
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </M>
  );
};

/* Image/video placeholder with corner crosshairs and scale-in. Renders real image when src is given. */
export const MediaPlaceholder = ({ src, alt = "", label = "Media", context = "", className = "", aspect = "aspect-[4/5]", hover = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 1.03 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 1, ease: EASE }}
      className={`group relative overflow-hidden ${aspect} ${className}`}
      style={{ background: "rgba(74,74,46,0.22)", border: "1px solid rgba(120,132,107,0.28)" }}
      data-testid={src ? "media-image" : "media-placeholder"}
    >
      {src ? (
        <img
          src={src}
          alt={alt || context}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[600ms] ${hover ? "group-hover:scale-[1.04]" : ""}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
        />
      ) : (
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center gap-2 transition-transform duration-[600ms] ${hover ? "group-hover:scale-[1.02]" : ""}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
        >
          <span className="eyebrow" style={{ color: "rgba(245,238,223,0.6)" }}>Media Placement</span>
          {context && <span className="font-body text-lg" style={{ color: "rgba(245,238,223,0.5)" }}>{context}</span>}
        </div>
      )}
      {[["top-4 left-4"], ["top-4 right-4"], ["bottom-4 left-4"], ["bottom-4 right-4"]].map((p, i) => (
        <svg key={i} className={`crosshair ${p}`} viewBox="0 0 14 14" style={{ position: "absolute" }} stroke="var(--sage)" strokeWidth="1">
          <line x1="7" y1="0" x2="7" y2="14" /><line x1="0" y1="7" x2="14" y2="7" />
        </svg>
      ))}
    </motion.div>
  );
};

/* Shared cycling index (respects reduced motion) */
export const useCycleIndex = (length, interval = 4200, delay = 0) => {
  const [idx, setIdx] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce || length < 2) return undefined;
    let timer;
    const start = setTimeout(() => {
      timer = setInterval(() => setIdx((i) => (i + 1) % length), interval);
    }, delay);
    return () => { clearTimeout(start); clearInterval(timer); };
  }, [reduce, length, interval, delay]);
  return idx;
};

/* Auto-cycling crossfade image: keeps changing on its own */
export const AutoCycleImage = ({ images = [], alt = "", interval = 4000, delay = 0, className = "" }) => {
  const [idx, setIdx] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce || images.length < 2) return undefined;
    let timer;
    const startTimer = setTimeout(() => {
      timer = setInterval(() => setIdx((i) => (i + 1) % images.length), interval);
    }, delay);
    return () => { clearTimeout(startTimer); clearInterval(timer); };
  }, [reduce, images.length, interval, delay]);
  return (
    <div className={`relative overflow-hidden ${className}`} data-testid="autocycle-image">
      {images.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt={i === 0 ? alt : ""}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1100ms]"
          style={{ opacity: i === idx ? 1 : 0, transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
        />
      ))}
    </div>
  );
};

/* Count up numeral */
export const CountUp = ({ to = 50, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setVal(to); return; }
    const controls = animate(mv, to, { duration: 1.2, ease: EASE, onUpdate: (v) => setVal(Math.round(v)) });
    return controls.stop;
  }, [inView, to, mv, reduce]);
  return <span ref={ref} className={className} data-testid="count-up">{val}</span>;
};

/* Circular unboxed emblem: elephant + palms + heritage arch, dark ink on a cream circle */
export const Logo = ({ size = 44, className = "" }) => (
  <span
    className={`inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${className}`}
    style={{ width: size, height: size, background: "var(--ivory)", boxShadow: "0 1px 10px rgba(0,0,0,0.18)" }}
    data-testid="logo"
  >
    <img
      src="/hitakari-logo.png"
      alt="Hitakari House"
      style={{ width: "116%", height: "116%", objectFit: "contain", display: "block" }}
    />
  </span>
);
